import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import { ClockIcon } from 'icons';
import { colors } from 'styles/colors';

import { MaintenanceValidationHistItem } from '../../types';
import { StatusBadge, BadgeDot } from '../../styles';
import { HistoryItemNote, HistoryDeadline } from './styles';
import { getRuleTitleKey, getRuleCauseKey } from '../../helpers/ruleLabels';

interface HistoryResultProps {
  item: MaintenanceValidationHistItem;
  timezoneArea?: string | null;
}

export function HistoryResult({ item, timezoneArea }: Readonly<HistoryResultProps>): JSX.Element {
  const { t } = useTranslation();
  const status = item.result?.status;

  if (status === 'positive') {
    const base = item.completed_at ?? item.start_time;
    const deadline = (timezoneArea ? moment.tz(base, timezoneArea) : moment(base)).add(24, 'hours');
    const isValidated = deadline.isBefore(timezoneArea ? moment.tz(timezoneArea) : moment());
    return (
      <div>
        <StatusBadge $variant="positive">
          <BadgeDot />
          {t('preValidacaoPositiva')}
        </StatusBadge>
        <HistoryItemNote>{t('validacaoPositivaSemInconsistencias')}</HistoryItemNote>
        {isValidated ? (
          <HistoryDeadline>
            {t('diagnosticoConcluido', {
              datetime: deadline.format('DD/MM/YYYY'),
              interpolation: { escapeValue: false },
            })}
          </HistoryDeadline>
        ) : (
          <HistoryDeadline>
            <ClockIcon size={11} color={colors.LightBlue} />
            {t('diagnosticoPrevistoAte', {
              datetime: deadline.format('DD/MM/YYYY, HH:mm'),
              interpolation: { escapeValue: false },
            })}
          </HistoryDeadline>
        )}
      </div>
    );
  }

  if (status === 'negative') {
    const ruleTitle = item.result?.rule_title;
    const titleKey = getRuleTitleKey(ruleTitle);
    const causeKey = getRuleCauseKey(ruleTitle);
    const reason = item.result?.reason;
    const causeText = causeKey ? ` ${t('possiveisCausas')}: ${t(causeKey)}.` : '';
    return (
      <div>
        <StatusBadge $variant="negative">
          <BadgeDot />
          {t('manutencaoInvalidaBadge')}
        </StatusBadge>
        <HistoryItemNote><strong>{t(titleKey)}</strong></HistoryItemNote>
        {reason && <HistoryItemNote>{`${reason}${causeText}`}</HistoryItemNote>}
        <HistoryItemNote>{t('manutencaoNaoConcluida')}</HistoryItemNote>
      </div>
    );
  }

  return <></>;
}
